import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { ConstructionMethod, CreateConstructionMethod, UpdateConstructionMethod } from '@estimator/shared';

export function useConstructionMethods() {
  return useQuery({
    queryKey: ['construction-methods'],
    queryFn: async () => {
      const { data } = await apiClient.get<ConstructionMethod[]>('/construction-methods');
      return data;
    },
  });
}

export function useConstructionMethod(id: string | null) {
  return useQuery({
    queryKey: ['construction-methods', id],
    queryFn: async () => {
      const { data } = await apiClient.get<ConstructionMethod>(`/construction-methods/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateConstructionMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateConstructionMethod) => {
      const { data } = await apiClient.post<ConstructionMethod>('/construction-methods', body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['construction-methods'] }),
  });
}

export function useUpdateConstructionMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateConstructionMethod & { id: string }) => {
      const { data } = await apiClient.patch<ConstructionMethod>(`/construction-methods/${id}`, body);
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['construction-methods'] });
      queryClient.invalidateQueries({ queryKey: ['construction-methods', vars.id] });
    },
  });
}

export function useDeleteConstructionMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/construction-methods/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['construction-methods'] }),
  });
}
